import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { css, styled } from "styled-components";

import { ArrowIcon, ArticleIcon, ChevronIcon, EditIcon, ListItemBox, VideoFileIcon } from "common/components";
import { useFetch } from "common/utilities/apiFetch";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { AddRemoveButton } from "domains/createMapDetail/components/keyIssueList/AddRemoveButton";
import {
  deleteTopicContent,
  scrapeTopicContentUrlFulfilled,
  setTopicContentFileContent,
} from "domains/customContentAndSources/actions";
import { ContentFileIcon } from "domains/customContentAndSources/components/ContentFileIcon";
import type { SimpleFileType } from "domains/customContentAndSources/components/fileType";
import { getFileTypeFromMime } from "domains/customContentAndSources/components/fileType";
import { getCustomContentIsDeleting } from "domains/customContentAndSources/selectors";
import type { CustomContent, CustomKnowledge, FileContent } from "domains/customContentAndSources/types";
import type { Term } from "domains/language/types";
import { useErrorModal } from "domains/modal/utils";
import { ExpandableKnowledgeItemWrapper } from "domains/publication/components/ExpandableKnowledgeItemWrapper";
import { EditListItem } from "domains/topic/components/EditListItem";
import { KnowledgeType } from "entities/knowledge/types";

const FileContentListItemContainer = styled.div``;

const iconStyle = css`
  width: 64px;
  min-width: 64px;
  height: 64px;
`;

const StyledFileIcon = styled(ContentFileIcon)`
  ${iconStyle}
`;

const StyledVideoIcon = styled(VideoFileIcon)`
  ${iconStyle}
`;

const StyledArticleIcon = styled(ArticleIcon)`
  ${iconStyle}
`;

const EditButton = styled.button`
  display: flex;
  width: 100%;
  font-weight: bold;
  align-items: center;
  cursor: pointer;
  transition: 0.3s;
  color: ${({ theme }) => theme.colors.textFade};
  background: none;
  border: none;
  height: 100%;
  &:hover {
    background-color: ${({ theme }) => theme.colors.activeButtonHoverBackground};
    color: ${({ theme }) => theme.colors.textBright};
  }
`;

const StyledDownloadIcon = styled(ArrowIcon)`
  transform: rotate(270deg);
  width: 22px;
  height: 22px;
`;

const ButtonContainer = styled.div<{ isEditing: boolean }>`
  display: flex;
  height: 100%;
  & > button {
    justify-content: center;
    width: 50%;
    padding: 0;
    text-align: center;
  }
`;

const StyledFileListItemBox = styled(ListItemBox)`
  cursor: initial;
`;

const rightActionStyle = css`
  display: flex;
  width: 100%;
  align-items: center;
  cursor: pointer;
  transition: 0.3s;
  color: ${({ theme }) => theme.colors.textFade};
  height: 100%;
  padding: 0 0 0 5px;
  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
  }
`;

const StyledButton = styled.button`
  ${rightActionStyle};
  border: none;
  background: none;
`;

const StyledChevronLink = styled.a`
  ${rightActionStyle};
`;

const StyledChevron = styled(ChevronIcon)`
  width: 14px;
  height: 14px;
`;

const StyledEditIcon = styled(EditIcon)`
  width: 12px;
  height: 12px;
  margin-right: 1rem;
`;

const isContentFile = (item: CustomContent): item is FileContent => item.type === "file";

type ContentListItemProps = {
  className?: string;
  content: CustomContent;
  isEditing?: boolean;
  onEditSelect?: () => void;
  topicId: string;
};

export const ContentListItem = ({
  content,
  topicId,
  className,
  onEditSelect,
  isEditing = false,
}: ContentListItemProps) => {
  if (isContentFile(content)) {
    return (
      <StyledFileListItemBox className={className}>
        <FileContentListItem
          fileContent={content}
          topicId={topicId}
          isEditing={isEditing}
          onEditSelect={onEditSelect}
        />
      </StyledFileListItemBox>
    );
  }
  const knowledgeItem = (
    <KnowledgeContentListItem knowledge={content} isEditing={isEditing} topicId={topicId} onEditSelect={onEditSelect} />
  );
  return <ListItemBox className={className}>{knowledgeItem}</ListItemBox>;
};

type KnowledgeContentListItemProps = {
  isEditing: boolean;
  knowledge: CustomKnowledge;
  onEditSelect?: () => void;
  topicId: string;
};

const KnowledgeContentListItem = ({ knowledge, isEditing, topicId, onEditSelect }: KnowledgeContentListItemProps) => {
  const dispatch = useDispatch();
  const isDeleting = useSelector(getCustomContentIsDeleting(knowledge.id));
  const translate = useTranslate();

  const onRemoveItem = () => {
    dispatch(deleteTopicContent({ id: knowledge.id, topicId }));
  };

  const onEditItem = () => {
    const reformattedKnowledge = {
      ...knowledge,
      publisher: knowledge.source?.name,
    };
    dispatch(
      scrapeTopicContentUrlFulfilled(reformattedKnowledge, {
        url: knowledge.link,
      }),
    );
    if (onEditSelect) {
      onEditSelect();
    }
  };

  return (
    <ExpandableKnowledgeItemWrapper
      variant="customKnowledge"
      id={knowledge.id}
      title={knowledge.title}
      summary={knowledge.summary}
      signals={knowledge.signals ?? []}
      shareLink={knowledge.link}
    >
      <FileContentListItemContainer>
        <EditListItem
          name={knowledge.title}
          subtitle={knowledge.source?.name}
          icon={knowledge.knowledgeType === KnowledgeType.video ? <StyledVideoIcon /> : <StyledArticleIcon />}
          actionElements={
            isEditing ? (
              <ButtonContainer isEditing={isEditing}>
                <EditButton onClick={onEditItem}>
                  <StyledEditIcon />
                  {translate("app.topic.actions.edit")}
                </EditButton>
                <AddRemoveButton isAdded isPending={isDeleting} onRemove={onRemoveItem} />
              </ButtonContainer>
            ) : null
          }
          rightElement={
            <StyledChevronLink
              href={knowledge.link}
              rel="noopener"
              target="_blank"
              // isActive={isEditing}
            >
              <StyledChevron />
            </StyledChevronLink>
          }
          actionElementsPosition={isEditing ? "below" : "inline"}
        />
      </FileContentListItemContainer>
    </ExpandableKnowledgeItemWrapper>
  );
};

type FileContentListItemProps = {
  className?: string;
  fileContent: FileContent;
  isArticle?: boolean;
  isEditing?: boolean;
  onEditSelect?: () => void;
  topicId: string;
};

const FileContentListItem = ({
  fileContent,
  topicId,
  className,
  onEditSelect,
  isEditing = false,
}: FileContentListItemProps) => {
  const dispatch = useDispatch();
  const siFetch = useFetch();
  const isUnmounting = useRef<boolean>(false);
  const isDeleting = useSelector(getCustomContentIsDeleting(fileContent.id));
  const [hasErrored, setHasErrored] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);
  const translate = useTranslate();

  const downloadFile = async () => {
    if (isUnmounting.current) {
      return;
    }
    try {
      setIsPending(true);
      setHasErrored(false);
      const downloadUrlResponse = await siFetch(`topic/${topicId}/attachment/${fileContent.id}/download`);
      const { url } = (await downloadUrlResponse.json()) as { url: string };
      const fileResponse = await fetch(url);
      if (!fileResponse.ok) {
        throw new Error("File response failed");
      }
      const fileBlob = await fileResponse.blob();
      if (isUnmounting.current) {
        return;
      }
      saveAs(fileBlob, fileContent.file_name);
      setIsPending(false);
    } catch {
      if (isUnmounting.current) {
        return;
      }
      setIsPending(false);
      setHasErrored(true);
    }
  };

  const onDownloadClick = () => void downloadFile();

  const onRemoveItem = () => {
    dispatch(deleteTopicContent({ id: fileContent.id, topicId }));
  };

  const onEditItem = () => {
    dispatch(setTopicContentFileContent(fileContent));
    if (onEditSelect) {
      onEditSelect();
    }
  };

  useErrorModal({ error: hasErrored });

  useEffect(
    () => () => {
      isUnmounting.current = true;
    },
    [],
  );

  return (
    <FileContentListItemContainer className={className}>
      <EditListItem
        name={fileContent.file_name}
        subtitle={subtitles[getFileTypeFromMime(fileContent.mimetype)]}
        icon={<StyledFileIcon mimetype={fileContent.mimetype} />}
        actionElements={
          isEditing ? (
            <ButtonContainer isEditing={isEditing}>
              <EditButton onClick={onEditItem}>
                <StyledEditIcon />
                {translate("app.topic.actions.edit")}
              </EditButton>
              <AddRemoveButton isAdded isPending={isDeleting} onRemove={onRemoveItem} />
            </ButtonContainer>
          ) : null
        }
        rightElement={
          <StyledButton onClick={onDownloadClick} disabled={isPending}>
            <StyledDownloadIcon />
          </StyledButton>
        }
        actionElementsPosition={isEditing ? "below" : "inline"}
      />
    </FileContentListItemContainer>
  );
};

const subtitles: Record<SimpleFileType, Term> = {
  audio: "app.custom_attachment.file_types.audio",
  document: "app.custom_attachment.file_types.document",
  generic: "app.custom_attachment.file_types.generic",
  image: "app.custom_attachment.file_types.image",
  pdf: "app.custom_attachment.file_types.pdf",
  presentation: "app.custom_attachment.file_types.presentation",
  spreadsheet: "app.custom_attachment.file_types.spreadsheet",
  video: "app.custom_attachment.file_types.video",
};
